import { IconButton } from "@circleco/compass/components/IconButton";
import { Menu } from "@circleco/compass/components/Menu";
import type { MenuOptions } from "@circleco/compass/components/Menu";
import { useState } from "react";

import { t } from "@/i18n-js/instance";

import { AdjustImages } from "../../AdjustImages";
import type { UploadedImage } from "../../store";
import { useHandleFileSelection } from "../../UploadImages/hooks/useHandleFileSelection";
import { useRemoveImage } from "../hooks/useRemoveImage";

interface UrlToFileProps {
  contentType: string;
  fileName: string;
  url: string;
}

const urlToFile = async ({ url, fileName, contentType }: UrlToFileProps) => {
  const response = await fetch(url, { cache: "no-store" });
  const blob = await response.blob();
  return new File([blob], fileName, {
    type: contentType,
    lastModified: new Date().getTime(),
  });
};

interface ImageOptionsDropdownProps {
  currentImage: UploadedImage;
}

export const ImageOptionsDropdown = ({
  currentImage,
}: ImageOptionsDropdownProps) => {
  const [isAdjustImagesOpen, setIsAdjustImagesOpen] = useState(false);
  const { removeImage } = useRemoveImage();
  const { handleFileSelection } = useHandleFileSelection(() =>
    setIsAdjustImagesOpen(true),
  );

  const handleCropImage = async () => {
    const file = await urlToFile({
      url: currentImage.original_url,
      fileName: currentImage.filename,
      contentType: "image/jpeg",
    });
    handleFileSelection([file]);
  };

  const options: MenuOptions = [
    {
      label: t(
        "image_space.post_modal.steps.create_post.image_options.crop_image",
      ),
      icon: "image",
      onClick: () => {
        void handleCropImage();
      },
    },
    {
      label: t("image_space.post_modal.steps.create_post.image_options.remove"),
      icon: "trash-can",
      onClick: () => {
        removeImage(currentImage);
      },
    },
  ];

  return (
    <>
      {isAdjustImagesOpen && (
        <AdjustImages
          onClose={() => setIsAdjustImagesOpen(false)}
          isOnTop
          editingImageSignedId={currentImage.signed_id}
        />
      )}
      <div className="absolute right-4 top-4">
        <Menu
          align="end"
          options={options}
          side="bottom"
          trigger={
            <IconButton
              aria-label={t(
                "image_space.post_modal.steps.create_post.image_options.label",
              )}
              icon="dot-menu"
              size="sm"
              type="button"
              variant="outline"
            />
          }
        />
      </div>
    </>
  );
};
